import {
  BackgroundColor,
  SectionContainer
} from "@silversea-ssc/ui-components-react";
import ConfiguredConfigure from "components/Algolia/ConfiguredConfigure";
import { ConfiguredInstantSearch } from "components/Algolia/ConfiguredInstantSearch";
import {
  CruisesSliderHitsProps,
  CruisesSliderResultsRenderer
} from "components/Algolia/CruisesSliderHits";
import { cruiseAttributesToRetrieve } from "components/FindYourCruise/Components/utils";
import FYCQueryBuilder, {
  FYCQuery
} from "components/FindYourCruise/FYCQueryBuilder";
import { useFYC } from "components/FindYourCruise/useFYC";
import React, { PropsWithChildren, useMemo } from "react";
import { useTrendingItems } from "react-instantsearch";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import { useLanguage } from "src/utils/localization/languages/I18nContext";
import { AlgoliaCruiseHit } from "types/algolia";

type Props = Omit<CruisesSliderHitsProps, "showDestination"> & {
  destinationWebCode: string | undefined;
  backgroundColor: BackgroundColor;
};

const TrendingItemsCruisesSlider: React.FC<PropsWithChildren<Props>> = ({
  destinationWebCode,
  ...props
}) => {
  const language = useLanguage();
  const { countryCode } = useGeolocation();
  const attributesToRetrieve = useMemo(
    () => cruiseAttributesToRetrieve(language, countryCode),
    [language, countryCode]
  );

  const { filterDefaultSearch, localizedSearchIndexName } = useFYC(undefined);
  if (!localizedSearchIndexName) return null;

  const fycFallbackQuery = new FYCQueryBuilder(filterDefaultSearch)
    .filterByDestinationWebCode(destinationWebCode)
    .filterByLoadStress(["Good", "Average"])
    .onlyAvailableCruises()
    .generate();

  const fycQuery = new FYCQueryBuilder(filterDefaultSearch)
    .filterByDestinationWebCode(destinationWebCode)
    .onlyAvailableCruises()
    .generate();

  return (
    <ConfiguredInstantSearch indexName={localizedSearchIndexName}>
      <ConfiguredConfigure
        distinct
        offset={0}
        length={20}
        attributesToRetrieve={attributesToRetrieve}
      />

      <TrendingItemsCruisesSliderRenderer
        {...props}
        dataContentName="trending_items_cruises_slider"
        destinationWebCode={destinationWebCode}
        searchQuery={fycQuery}
        fallbackSearchQuery={fycFallbackQuery}
      />
    </ConfiguredInstantSearch>
  );
};

type TrendingItemsCruisesSliderRendererProps = Omit<
  Props,
  "variant" | "swiperConfig"
> & {
  searchQuery: FYCQuery;
  fallbackSearchQuery: FYCQuery;
  dataContentName: string;
};

const TrendingItemsCruisesSliderRenderer: React.FC<
  PropsWithChildren<TrendingItemsCruisesSliderRendererProps>
> = ({
  destinationWebCode,
  searchQuery,
  fallbackSearchQuery,
  dataContentName,
  children,
  ...props
}) => {
  const { items, sendEvent } = useTrendingItems<AlgoliaCruiseHit>({
    facetName: "destinationWebCodes",
    facetValue: destinationWebCode,
    queryParameters: { filters: searchQuery },
    fallbackParameters: { filters: fallbackSearchQuery }
  });

  if (items.length == 0) return null;

  return (
    <SectionContainer
      backgroundColor={props.backgroundColor}
      spacingTop="regular"
      spacingBottom="regular"
      dataContentName={dataContentName}
    >
      {children}
      <CruisesSliderResultsRenderer
        {...props}
        variant={undefined}
        items={items}
        refinements={undefined}
        filters={undefined}
        sendEvent={sendEvent}
        showDestination={false}
        variantColor={props.backgroundColor == "white" ? "neutral01" : "white"}
      />
    </SectionContainer>
  );
};

export default TrendingItemsCruisesSlider;
